import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Zones d\'intervention', item: 'https://fenetre-paris15.fr/zones-intervention-paris-15/' },
]);

const quartiers = [
  {
    name: 'Vaugirard',
    desc: `Cœur historique du 15ᵉ, Vaugirard est le plus ancien village de l'arrondissement. Le bâti est majoritairement haussmannien et post-haussmannien, avec de nombreuses fenêtres bois d'origine et des menuiseries PVC de première rénovation. Les immeubles de la rue de Vaugirard et des rues adjacentes présentent des configurations classiques : fenêtres à deux vantaux, volets roulants récents et quelques baies vitrées sur les rez-de-chaussée commerciaux.`,
  },
  {
    name: 'Grenelle',
    desc: `Ancienne plaine maraîchère devenue quartier mixte, Grenelle s'étend de la Seine au métro La Motte-Picquet. Le bâti alterne entre immeubles haussmanniens anciens (côté rue du Commerce) et résidences modernes en bord de Seine. Les menuiseries sont variées : bois sur l'ancien, aluminium et PVC sur le récent. Les courants d'air et l'humidité du fleuve sollicitent particulièrement les joints d'étanchéité.`,
  },
  {
    name: 'Javel',
    desc: `Ancien site des usines Citroën reconverti à partir des années 1990, Javel est aujourd'hui un quartier dynamique mêlant immeubles de bureaux, résidences contemporaines et le vaste Parc André-Citroën. Les menuiseries y sont majoritairement récentes : aluminium à rupture de pont thermique et PVC haut de gamme. Les pannes typiques concernent la quincaillerie moderne et les grandes surfaces vitrées.`,
  },
  {
    name: 'Beaugrenelle',
    desc: `Également connu sous le nom de Front de Seine, Beaugrenelle est un quartier de tours et d'immeubles modernes édifiés dans les années 1970-1990. La particularité architecturale : de très grandes baies vitrées coulissantes. Ces menuiseries de grande dimension subissent des contraintes mécaniques importantes. Les pannes de galets, de rails et de serrures y sont fréquentes.`,
  },
  {
    name: 'Saint-Lambert',
    desc: `Quartier résidentiel calme situé au sud du 15ᵉ, Saint-Lambert conserve un caractère villageois avec des pavillons, des immeubles de taille modeste et quelques résidences récentes. Les menuiseries y sont diverses : bois pour l'ancien, PVC pour les rénovations. Les pannes liées au vieillissement de la quincaillerie dominent.`,
  },
  {
    name: 'Necker',
    desc: `Quartier institutionnel autour de l'hôpital Necker-Enfants malades, mêlant immeubles anciens et bâtiments hospitaliers. Les fenêtres sont majoritairement en bois et PVC. L'entretien régulier de la quincaillerie est essentiel dans ces immeubles souvent anciens où les menuiseries peuvent avoir plusieurs décennies.`,
  },
  {
    name: 'Convention',
    desc: `Artère commerçante majeure du 15ᵉ, la rue de la Convention traverse le quartier du même nom. Les immeubles sont majoritairement haussmanniens ou post-haussmanniens, avec des commerces en rez-de-chaussée. Les menuiseries PVC ont souvent remplacé le bois d'origine. Les pannes de fermeture et d'étanchéité sont courantes.`,
  },
  {
    name: 'Commerce',
    desc: `Le quartier Commerce s'organise autour de la rue du Commerce, axe piétonnier et commerçant. Immeubles de standing avec des menuiseries de qualité, souvent rénovées en PVC ou aluminium. Les fenêtres y sont généralement en bon état mais la quincaillerie peut nécessiter un entretien régulier.`,
  },
  {
    name: 'Cambronne',
    desc: `Secteur calme autour de la place Cambronne, ce quartier résidentiel compte de nombreux immeubles haussmanniens avec portes-fenêtres et volets. Les crémones anciennes et les mécanismes de volet sont les interventions les plus fréquentes, ainsi que le réglage de vantaux qui ont travaillé avec le temps.`,
  },
  {
    name: 'Pasteur',
    desc: `Carrefour stratégique où se croisent les lignes de métro 6 et 12, le quartier Pasteur mélange bureaux et résidentiel. Les immeubles de bureaux sont souvent équipés de menuiseries aluminium, tandis que les résidences privilégient le PVC. La quincaillerie de ces ouvrants très sollicités nécessite un entretien régulier.`,
  },
  {
    name: 'Falguière',
    desc: `Ancien quartier d'ateliers d'artistes, Falguière a conservé quelques ateliers reconvertis en lofts et des immeubles aux menuiseries atypiques (verrières, grandes baies d'atelier). Ces configurations particulières demandent une expertise spécifique que DF PRO maîtrise. Les crémones sur mesure et les mécanismes anciens sont notre quotidien.`,
  },
  {
    name: 'Front de Seine',
    desc: `Ensemble de tours emblématiques des années 1970 le long de la Seine, le Front de Seine se caractérise par de très grandes surfaces vitrées et des baies coulissantes. Les contraintes de poids et d'exposition au vent nécessitent une quincaillerie renforcée. DF PRO intervient régulièrement sur ces menuiseries spécifiques.`,
  },
  {
    name: 'Porte de Versailles',
    desc: `Quartier en pleine mutation autour du Parc des Expositions, la Porte de Versailles compte désormais de nombreuses résidences récentes, hôtels et le Palais des Sports. Les menuiseries y sont modernes (PVC, aluminium), mais la forte densité et l'usage intensif génèrent des besoins d'entretien réguliers.`,
  },
];

export default function ZonesPage() {
  return (
    <Layout>
      <SEOHead
        title="Réparation Fenêtre dans tout le 15ᵉ — Quartiers | DF PRO"
        description="DF PRO intervient dans tous les quartiers du 15ᵉ : Vaugirard, Grenelle, Javel, Beaugrenelle, Convention… Devis gratuit 24/48h. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/zones-intervention-paris-15/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Zones d'intervention</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark mb-4">Nos zones d'intervention dans le 15ᵉ arrondissement</h1>
            <p className="text-brand-text/70 text-base max-w-3xl mx-auto">
              DF PRO intervient dans l'ensemble du 15ᵉ arrondissement de Paris. Nous connaissons chaque quartier, chaque type de bâti et chaque spécificité architecturale. Voici le détail de nos zones d'intervention.
            </p>
          </div>

          {/* Image d'en-tête */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden">
              <img
                src="/images/dfpro-zones-header.webp"
                alt="Quartier résidentiel du 15ᵉ arrondissement mêlant immeubles haussmanniens et modernes — DF PRO Paris 15"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-12">
            {quartiers.map((q) => (
              <div key={q.name} className="bg-brand-mint/30 rounded-xl p-5 md:p-6">
                <h2 className="font-heading font-bold text-brand-dark text-lg mb-2">{q.name}</h2>
                <p className="text-brand-text/60 text-sm leading-relaxed">{q.desc}</p>
              </div>
            ))}
          </div>

          {/* Liens vers pages quartier détaillées */}
          <div className="text-center bg-gradient-to-r from-brand-gradientFrom to-brand-gradientTo rounded-2xl p-8 md:p-10 text-white">
            <h2 className="font-heading text-2xl font-bold mb-4">Découvrez nos pages quartier détaillées</h2>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <Link to="/quartier-javel/" className="bg-white text-brand-dark font-semibold rounded-full px-6 py-3 text-sm hover:opacity-90 transition-opacity whitespace-nowrap">Quartier Javel</Link>
              <Link to="/quartier-beaugrenelle/" className="bg-white text-brand-dark font-semibold rounded-full px-6 py-3 text-sm hover:opacity-90 transition-opacity whitespace-nowrap">Quartier Beaugrenelle</Link>
              <Link to="/quartier-grenelle/" className="bg-white text-brand-dark font-semibold rounded-full px-6 py-3 text-sm hover:opacity-90 transition-opacity whitespace-nowrap">Quartier Grenelle</Link>
            </div>
            <Link to="/devis-fenetre-paris-15/" className="inline-flex items-center gap-2 text-white border-2 border-white/50 hover:border-white rounded-full px-6 py-3 text-sm font-semibold transition-all whitespace-nowrap">
              <i className="ri-file-text-line"></i> Demander un devis
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}